/**
 * The FIXED status → colour map (docs/14 "Status chip"). One place, so a status reads the same
 * colour on every screen; colour is never the only signal (chips also carry an icon and text).
 *
 *   grey    draft, inactive, history
 *   cyan    in operation, open, live
 *   purple  locked, published by the platform, network
 *   lime    settled, done, success
 *   red     disputed, failed, refused
 *   amber   hold, pending, waiting
 */
export type StatusTone = 'grey' | 'cyan' | 'purple' | 'lime' | 'red' | 'amber';

export const STATUS_TONES: Readonly<Record<string, StatusTone>> = Object.freeze({
  // docs/14 anchors
  draft: 'grey',
  in_operation: 'cyan',
  locked: 'purple',
  finance_locked: 'purple',
  settled: 'lime',
  disputed: 'red',
  hold: 'amber',
  // generic lifecycle
  active: 'lime',
  pending: 'amber',
  in_review: 'amber',
  under_review: 'amber',
  suspended: 'amber',
  blacklisted: 'red',
  prospect: 'grey',
  merged_into: 'grey',
  approved: 'lime',
  declined: 'red',
  withdrawn: 'grey',
  cleared: 'lime',
  confirmed: 'red',
  monitor: 'amber',
  expired: 'grey',
  revoked: 'grey',
  failed: 'red',
  queued: 'amber',
  sent: 'lime',
  replayed: 'grey',
  open: 'cyan',
  // community
  published: 'cyan',
  cancelled: 'red',
  completed: 'lime',
  archived: 'grey',
  registered: 'cyan',
  waitlisted: 'amber',
  attended: 'lime',
  no_show: 'grey',
});

export function toneOf(status: string): StatusTone {
  return STATUS_TONES[status] ?? 'grey';
}

/** CSS colour of a tone (a brand token, so it follows :root / .dark / .theme-oex). */
export const TONE_COLOR: Readonly<Record<StatusTone, string>> = Object.freeze({
  grey: 'var(--oex-grey, hsl(var(--muted-foreground)))',
  cyan: 'var(--oex-cyan)',
  purple: 'var(--oex-purple)',
  lime: 'var(--oex-lime)',
  red: 'hsl(var(--destructive))',
  amber: 'var(--oex-amber)',
});
